import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../api/axios';

// Types
type Facility = {
  ccn: string;
  org_name: string;
  dba_name: string;
  affiliation: string | null;
  total_billed: number | null;
  total_paid: number | null;
  state?: string;
  scheme_count?: number;
  total_instances?: number;
};

type OwnerWithFacilities = {
  owner_name: string;
  owner_type: string;
  ownership_percentage: number;
  total_facilities: number;
  case_facilities: number;
  other_facilities: Facility[];
  total_billed: number;
  total_paid: number;
  scheme_instances: number;
};

interface AddFacilityModalProps {
  open: boolean;
  onClose: () => void;
  onAddFacility: (ccn: string) => void;
  currentFacilities: Facility[];
  authHeaders: { Authorization: string };
  formatCurrency: (val: string | number | null | undefined) => string;
  caseId: string;
}

export default function AddFacilityModal({
  open,
  onClose,
  onAddFacility,
  currentFacilities,
  authHeaders,
  formatCurrency,
  caseId
}: AddFacilityModalProps) {
  const [activeTab, setActiveTab] = useState(0);

  // Broad search states
  const [searchQ, setSearchQ] = useState('');
  const [searchRes, setSearchRes] = useState<Facility[]>([]);
  const [searching, setSearching] = useState(false);

  // Owner-based search states
  const [ownerFacilities, setOwnerFacilities] = useState<OwnerWithFacilities[]>([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [ownersError, setOwnersError] = useState<string | null>(null);

  // Broad facility search function
  const runBroadSearch = async () => {
    if (!searchQ.trim() || searchQ.length < 2) {
      setSearchRes([]);
      return;
    }
    setSearching(true);

    try {
      const { data } = await api.get(`/snfs/facilities/search/?q=${encodeURIComponent(searchQ.trim())}&case_id=${caseId}`, { headers: authHeaders });

      // Filter out facilities already in the case (API should already do this, but double check)
      const current = new Set(currentFacilities.map(f => f.ccn));
      setSearchRes(data.filter((r: Facility) => !current.has(r.ccn)));
    } catch (err) {
      console.error('Failed to search facilities:', err);
      setSearchRes([]);
    } finally {
      setSearching(false);
    }
  };

  // Load facilities from current owners
  const loadOwnerFacilities = async () => {
    setLoadingOwners(true);
    setOwnersError(null);

    try {
      const { data } = await api.get(`/snfs/scoping/owner_facilities/?case_id=${caseId}`, { headers: authHeaders });
      setOwnerFacilities(data);
    } catch (err: any) {
      console.error('Failed to load owner facilities:', err);
      setOwnersError(err.response?.data?.detail || 'Failed to load owner facilities');
      setOwnerFacilities([]);
    } finally {
      setLoadingOwners(false);
    }
  };

  // Load owner facilities when tab changes to owner-based search
  useEffect(() => {
    if (activeTab === 1 && open && ownerFacilities.length === 0) {
      loadOwnerFacilities();
    }
  }, [activeTab, open]);

  // Reset states when modal closes
  const handleClose = () => {
    setSearchQ('');
    setSearchRes([]);
    setOwnerFacilities([]);
    setActiveTab(0);
    setOwnersError(null);
    onClose();
  };

  const handleAddFacility = (ccn: string) => {
    onAddFacility(ccn);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Add Facilities to Case
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<SearchIcon />} label="Broad Search" />
          <Tab icon={<PeopleIcon />} label="From Current Owners" />
        </Tabs>

        {/* Tab 1: Broad Search */}
        {activeTab === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search all facilities by CCN, organization name, or DBA name
            </Typography>

            <TextField
              fullWidth
              placeholder="Search by CCN, name…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runBroadSearch()}
              sx={{ mb: 2 }}
            />

            {searching ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </Box>
            ) : searchRes.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>CCN</TableCell>
                    <TableCell>Org Name</TableCell>
                    <TableCell>DBA</TableCell>
                    <TableCell>Affiliation</TableCell>
                    <TableCell>Billed</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchRes.map((f) => (
                    <TableRow key={f.ccn}>
                      <TableCell>{f.ccn}</TableCell>
                      <TableCell>{f.org_name}</TableCell>
                      <TableCell>{f.dba_name}</TableCell>
                      <TableCell>{f.affiliation || '—'}</TableCell>
                      <TableCell>{formatCurrency(f.total_billed)}</TableCell>
                      <TableCell>{formatCurrency(f.total_paid)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => handleAddFacility(f.ccn)}
                        >
                          Add
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : searchQ && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No facilities found for "{searchQ}"
              </Typography>
            )}
          </Box>
        )}

        {/* Tab 2: From Current Owners */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add facilities from owners of your current case facilities. Preview shows other facilities they own with billing totals and scheme instances.
            </Typography>

            {loadingOwners ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </Box>
            ) : ownersError ? (
              <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 4 }}>
                {ownersError}
              </Typography>
            ) : ownerFacilities.length > 0 ? (
              <Box>
                {ownerFacilities.map((owner, idx) => (
                  <Card key={idx} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {owner.owner_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {owner.case_facilities} of {owner.total_facilities} facilities in current case
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Total Billed: {formatCurrency(owner.total_billed)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Total Paid: {formatCurrency(owner.total_paid)}
                          </Typography>
                          <Typography variant="body2" color="primary.main">
                            {owner.scheme_instances} scheme {owner.scheme_instances === 1 ? 'instance' : 'instances'}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Other Facilities ({owner.other_facilities.length})
                      </Typography>

                      {owner.other_facilities.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>CCN</TableCell>
                              <TableCell>Org Name</TableCell>
                              <TableCell>State</TableCell>
                              <TableCell>Billed</TableCell>
                              <TableCell>Paid</TableCell>
                              <TableCell>Scheme Instances</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {owner.other_facilities
                              .sort((a, b) => {
                                // Sort by scheme instances descending, then by org_name ascending
                                const schemeCountA = a.scheme_count || 0;
                                const schemeCountB = b.scheme_count || 0;
                                if (schemeCountB !== schemeCountA) {
                                  return schemeCountB - schemeCountA;
                                }
                                return (a.org_name || '').localeCompare(b.org_name || '');
                              })
                              .map((facility) => (
                              <TableRow key={facility.ccn}>
                                <TableCell>{facility.ccn}</TableCell>
                                <TableCell>{facility.org_name}</TableCell>
                                <TableCell>{facility.state || '—'}</TableCell>
                                <TableCell>{formatCurrency(facility.total_billed)}</TableCell>
                                <TableCell>{formatCurrency(facility.total_paid)}</TableCell>
                                <TableCell>
                                  {(facility.scheme_count || 0) > 0 ? (
                                    <Chip
                                      label={`${facility.scheme_count || 0}/${facility.total_instances || 0}`}
                                      size="small"
                                      color="primary"
                                    />
                                  ) : `0/${facility.total_instances || 0}`}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    onClick={() => handleAddFacility(facility.ccn)}
                                  >
                                    Add
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No other facilities found for this owner
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No owner data available. Make sure you have facilities assigned to this case and ownership reports generated.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}